<script setup lang="ts">
  import { computed, onUnmounted, ref, watch } from 'vue';
  import { useEventListener } from '@vueuse/core';
  import SectorsService from '@/api/services/sectors.service';
  import TablesService from '@/api/services/tables.service';
  import FloorPlanCanvas from './FloorPlanCanvas.vue';
  import FloorPlanSectorTabs from './FloorPlanSectorTabs.vue';
  import FloorPlanUnplacedTray from './FloorPlanUnplacedTray.vue';
  import SectorModal from '@/components/modals/tables/SectorModal.vue';
  import NewTableModal from '@/components/modals/NewTableModal.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { ISector } from '@/interfaces/ISector';
  import { ITable, ITablesLayoutEntry } from '@/interfaces/ITable';
  import { i18n } from '@/plugins/i18n';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const DEFAULT_TABLE_SIZE = 1;

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const modals = useModal();

  const sectors = ref<ISector[] | null>(null);
  const tables = ref<ITable[] | null>(null);
  const draftEntries = ref<ITablesLayoutEntry[]>([]);
  // Non-reactive baseline snapshot; replaced together with draftEntries.
  let baseline = new Map<number, ITablesLayoutEntry>();

  const activeSectorId = ref<number | null>(null);
  const selectedTableId = ref<number | null>(null);
  const saving = ref(false);

  const canvasRef = ref<InstanceType<typeof FloorPlanCanvas> | null>(null);

  //#region Loading / draft state

  function toEntry(table: ITable): ITablesLayoutEntry {
    // Stored w/h are only meaningful while the table is placed: for tray
    // tables the DB holds the column default (2), which would otherwise
    // override the editor's small placement default.
    const placed =
      table.sectorId != null && table.posX != null && table.posY != null;
    return {
      tableId: table.id,
      sectorId: table.sectorId ?? null,
      posX: table.posX ?? null,
      posY: table.posY ?? null,
      w: placed ? table.w ?? DEFAULT_TABLE_SIZE : DEFAULT_TABLE_SIZE,
      h: placed ? table.h ?? DEFAULT_TABLE_SIZE : DEFAULT_TABLE_SIZE,
      shape: table.shape ?? 'square',
    };
  }

  function resetDraft() {
    draftEntries.value = (tables.value ?? []).map(toEntry);
    baseline = new Map(draftEntries.value.map((e) => [e.tableId, { ...e }]));
  }

  async function loadData() {
    const restaurantId = restaurantStore.restaurant?.id;
    const branchId = restaurantStore.selectedBranch?.id;
    if (!restaurantId || !branchId) return;

    try {
      const [sectorsList, tablesList] = await Promise.all([
        SectorsService.getAll(restaurantId, branchId),
        TablesService.getAll(restaurantId, branchId),
      ]);

      // Ignore stale responses (restaurant/branch switched while loading).
      if (
        restaurantStore.restaurant?.id !== restaurantId ||
        restaurantStore.selectedBranch?.id !== branchId
      ) {
        return;
      }

      setSectors(sectorsList);
      setTables(tablesList);
      selectedTableId.value = null;
      resetDraft();

      if (!sectorsList.some((s) => s.id === activeSectorId.value)) {
        activeSectorId.value = sectorsList[0]?.id ?? null;
      }
    } catch (error) {
      ui.serverError(error, i18n.t('floorPlan.errorLoading') as string);
    }
  }

  /** Single write-path for sectors: keeps the store copy in sync. */
  function setSectors(list: ISector[] | null) {
    sectors.value = list;
    restaurantStore.setSectors(list);
  }

  /** Single write-path for tables: keeps the store copy in sync. */
  function setTables(list: ITable[] | null) {
    tables.value = list;
    restaurantStore.setTables(list);
  }

  //#endregion

  //#region Derived state

  const activeSector = computed(
    () => sectors.value?.find((s) => s.id === activeSectorId.value) ?? null
  );

  const tablesById = computed(
    () => new Map((tables.value ?? []).map((t) => [t.id, t]))
  );

  const activeSectorEntries = computed(() =>
    draftEntries.value.filter(
      (e) =>
        e.sectorId === activeSectorId.value &&
        e.posX !== null &&
        e.posY !== null
    )
  );

  const unplacedTables = computed(() => {
    // Entries pointing at a sector that no longer exists (e.g. deleted by
    // another admin) count as unplaced so their tables never disappear.
    const sectorIds = new Set((sectors.value ?? []).map((s) => s.id));
    const placedIds = new Set(
      draftEntries.value
        .filter((e) => e.sectorId !== null && sectorIds.has(e.sectorId))
        .map((e) => e.tableId)
    );
    return (tables.value ?? []).filter(
      (t) => t.enabled && !placedIds.has(t.id)
    );
  });

  const changedEntries = computed(() =>
    draftEntries.value.filter((entry) => {
      const base = baseline.get(entry.tableId);
      return (
        !base ||
        base.sectorId !== entry.sectorId ||
        base.posX !== entry.posX ||
        base.posY !== entry.posY ||
        base.w !== entry.w ||
        base.h !== entry.h ||
        base.shape !== entry.shape
      );
    })
  );

  const dirty = computed(() => changedEntries.value.length > 0);

  // Registered after `dirty` (it runs immediately on setup).
  watch(
    [
      () => restaurantStore.restaurant?.id,
      () => restaurantStore.selectedBranch?.id,
    ],
    () => {
      // The branch selector doesn't navigate, so the route-leave guard can't
      // intercept it: warn that the unsaved draft was dropped.
      if (dirty.value) {
        ui.showToast(
          AlertType.Info,
          i18n.t('floorPlan.draftDiscarded') as string
        );
      }
      sectors.value = null;
      tables.value = null;
      draftEntries.value = [];
      baseline = new Map();
      activeSectorId.value = null;
      loadData();
    },
    { immediate: true }
  );

  //#endregion

  //#region Draft mutations

  function updateEntry(tableId: number, patch: Partial<ITablesLayoutEntry>) {
    const index = draftEntries.value.findIndex((e) => e.tableId === tableId);
    if (index === -1) return;

    const updated = { ...draftEntries.value[index], ...patch };
    if (updated.sectorId === null) {
      updated.posX = null;
      updated.posY = null;
    }
    draftEntries.value.splice(index, 1, updated);
  }

  function onEntryChange(payload: {
    tableId: number;
    patch: Partial<ITablesLayoutEntry>;
  }) {
    updateEntry(payload.tableId, payload.patch);
  }

  function onToggleShape(tableId: number) {
    const entry = draftEntries.value.find((e) => e.tableId === tableId);
    if (!entry) return;
    updateEntry(tableId, {
      shape: entry.shape === 'round' ? 'square' : 'round',
    });
  }

  function onUnplace(tableId: number) {
    updateEntry(tableId, { sectorId: null });
    if (selectedTableId.value === tableId) selectedTableId.value = null;
  }

  function isAreaFree(
    sectorId: number,
    posX: number,
    posY: number,
    w: number,
    h: number,
    excludeTableId?: number
  ): boolean {
    for (const entry of draftEntries.value) {
      if (entry.tableId === excludeTableId) continue;
      if (entry.sectorId !== sectorId) continue;
      if (entry.posX === null || entry.posY === null) continue;
      const overlaps =
        posX < entry.posX + entry.w &&
        entry.posX < posX + w &&
        posY < entry.posY + entry.h &&
        entry.posY < posY + h;
      if (overlaps) return false;
    }
    return true;
  }

  function onTrayDrop(payload: {
    tableId: number;
    clientX: number;
    clientY: number;
  }) {
    const sector = activeSector.value;
    const canvas = canvasRef.value;
    if (!sector || !canvas || saving.value) return;

    const cell = canvas.cellAtPoint(payload.clientX, payload.clientY);
    if (!cell) return;

    // Keep the size/shape the table already had (e.g. unplace + re-place),
    // clamped to the target grid.
    const entry = draftEntries.value.find((e) => e.tableId === payload.tableId);
    const w = Math.min(entry?.w ?? DEFAULT_TABLE_SIZE, sector.gridCols);
    const h = Math.min(entry?.h ?? DEFAULT_TABLE_SIZE, sector.gridRows);
    const posX = Math.min(Math.max(cell.x, 0), sector.gridCols - w);
    const posY = Math.min(Math.max(cell.y, 0), sector.gridRows - h);

    if (!isAreaFree(sector.id, posX, posY, w, h)) {
      ui.showToast(AlertType.Info, i18n.t('floorPlan.dropOccupied') as string);
      return;
    }

    updateEntry(payload.tableId, {
      sectorId: sector.id,
      posX,
      posY,
      w,
      h,
    });
    selectedTableId.value = payload.tableId;
  }

  //#endregion

  //#region Save / discard / leave guards

  async function save() {
    if (!dirty.value || saving.value) return;

    const restaurantId = restaurantStore.restaurant?.id;
    const branchId = restaurantStore.selectedBranch?.id;
    if (!restaurantId || !branchId) return;

    try {
      saving.value = true;

      await TablesService.saveLayout({
        restaurantId,
        branchId,
        entries: changedEntries.value,
      });

      ui.showToast(AlertType.Success, i18n.t('floorPlan.saved') as string);
      await loadData();
    } catch (error) {
      ui.serverError(error, i18n.t('floorPlan.errorSaving') as string);
    } finally {
      saving.value = false;
    }
  }

  function discard() {
    resetDraft();
    selectedTableId.value = null;
  }

  async function confirmLeave(): Promise<boolean> {
    if (!dirty.value) return true;

    return await modals.confirm({
      title: i18n.t('floorPlan.leave.title') as string,
      message: i18n.t('floorPlan.leave.message') as string,
      acceptText: i18n.t('floorPlan.leave.confirm') as string,
      cancelText: i18n.t('cancel') as string,
      acceptColor: 'error',
    });
  }

  useEventListener(window, 'beforeunload', (event: BeforeUnloadEvent) => {
    if (!dirty.value) return;
    event.preventDefault();
    event.returnValue = '';
  });

  // Route-leave guard: the page is a single-instance route component, so the
  // module-scoped hook (declared in the plain <script> block) is safe.
  activeLeaveGuard = confirmLeave;
  onUnmounted(() => {
    activeLeaveGuard = null;
  });

  //#endregion

  //#region Sector actions

  function showSectorModal(sectorToEdit: ISector | null) {
    const restaurantId = restaurantStore.restaurant?.id;
    const branchId = restaurantStore.selectedBranch?.id;
    if (!restaurantId || !branchId) return;

    modals.show(
      SectorModal,
      { restaurantId, branchId, sectorToEdit },
      {
        name: 'sector-modal',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 480,
        clickToClose: false,
      },
      {
        saved: (sector: ISector) => {
          reloadSectors(sector.id);
        },
      }
    );
  }

  async function reloadSectors(selectSectorId?: number) {
    const restaurantId = restaurantStore.restaurant?.id;
    const branchId = restaurantStore.selectedBranch?.id;
    if (!restaurantId || !branchId) return;

    try {
      setSectors(await SectorsService.getAll(restaurantId, branchId));
      reconcileDraftWithSectors();
      if (selectSectorId != null) activeSectorId.value = selectSectorId;
      if (!sectors.value?.some((s) => s.id === activeSectorId.value)) {
        activeSectorId.value = sectors.value?.[0]?.id ?? null;
      }
    } catch (error) {
      ui.serverError(error, i18n.t('floorPlan.errorLoading') as string);
    }
  }

  /**
   * Re-fits unsaved draft placements after sector changes: the server-side
   * grid shrink guard only validates persisted positions, so a draft entry
   * can be left out of bounds (or pointing at a sector deleted elsewhere).
   */
  function reconcileDraftWithSectors() {
    const sectorsById = new Map((sectors.value ?? []).map((s) => [s.id, s]));

    for (const entry of [...draftEntries.value]) {
      if (entry.sectorId === null) continue;

      const sector = sectorsById.get(entry.sectorId);
      if (!sector) {
        updateEntry(entry.tableId, { sectorId: null });
        continue;
      }

      if (entry.posX === null || entry.posY === null) continue;
      const posX = Math.min(entry.posX, sector.gridCols - entry.w);
      const posY = Math.min(entry.posY, sector.gridRows - entry.h);
      if (posX === entry.posX && posY === entry.posY) continue;

      const fits =
        posX >= 0 &&
        posY >= 0 &&
        isAreaFree(sector.id, posX, posY, entry.w, entry.h, entry.tableId);
      if (fits) {
        updateEntry(entry.tableId, { posX, posY });
      } else {
        updateEntry(entry.tableId, { sectorId: null });
      }
    }
  }

  async function deleteSector(sector: ISector) {
    const confirmed = await modals.confirm({
      title: i18n.t('floorPlan.sector.delete') as string,
      message: i18n.t('floorPlan.sector.deleteWarning') as string,
      acceptText: i18n.t('delete') as string,
      cancelText: i18n.t('cancel') as string,
      acceptColor: 'error',
    });
    if (!confirmed) return;

    try {
      await SectorsService.delete(sector.id);

      // The server unplaced the sector's tables: mirror it in the draft, the
      // baseline and the source table list (so a later discard/resetDraft
      // doesn't resurrect the deleted sector) without touching other
      // unsaved changes.
      for (const entry of draftEntries.value) {
        if (entry.sectorId === sector.id) {
          updateEntry(entry.tableId, { sectorId: null });
        }
      }
      for (const base of baseline.values()) {
        if (base.sectorId === sector.id) {
          base.sectorId = null;
          base.posX = null;
          base.posY = null;
        }
      }
      setTables(
        (tables.value ?? []).map((t) =>
          t.sectorId === sector.id
            ? { ...t, sectorId: null, posX: null, posY: null }
            : t
        )
      );
      // Baseline is non-reactive: force the dirty diff to recompute.
      draftEntries.value = [...draftEntries.value];

      await reloadSectors();
    } catch (error) {
      ui.serverError(error, i18n.t('floorPlan.sector.errorDeleting') as string);
    }
  }

  const reordering = ref(false);
  async function moveSector(payload: { sector: ISector; direction: -1 | 1 }) {
    const restaurantId = restaurantStore.restaurant?.id;
    const branchId = restaurantStore.selectedBranch?.id;
    if (!restaurantId || !branchId || !sectors.value || reordering.value)
      return;

    const ids = sectors.value.map((s) => s.id);
    const index = ids.indexOf(payload.sector.id);
    const target = index + payload.direction;
    if (index === -1 || target < 0 || target >= ids.length) return;

    [ids[index], ids[target]] = [ids[target], ids[index]];

    try {
      reordering.value = true;
      const reordered = await SectorsService.reorder(
        restaurantId,
        branchId,
        ids
      );
      setSectors([...reordered].sort((a, b) => a.index - b.index));
    } catch (error) {
      ui.serverError(
        error,
        i18n.t('floorPlan.sector.errorReordering') as string
      );
    } finally {
      reordering.value = false;
    }
  }

  //#endregion

  //#region New table

  function showNewTableModal() {
    const restaurantId = restaurantStore.restaurant?.id;
    const branchId = restaurantStore.selectedBranch?.id;
    if (!restaurantId || !branchId) return;

    modals.show(
      NewTableModal,
      {
        table: {
          restaurantId,
          branchId,
          name: '',
          stringId: '',
          index: tables.value?.length ?? 0,
        },
      },
      {
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
      },
      {
        saved: (table: ITable) => {
          // The new table lands in the tray; keep unsaved layout changes.
          if (!tables.value) return;
          setTables([...tables.value, table]);
          draftEntries.value.push(toEntry(table));
          baseline.set(table.id, toEntry(table));
        },
      }
    );
  }

  //#endregion
</script>

<script lang="ts">
  // vue-router 3 has no onBeforeRouteLeave composable: the in-component guard
  // must be declared as an option. It reaches the setup state through this
  // module-scoped hook, registered by the <script setup> block above.
  let activeLeaveGuard: (() => Promise<boolean>) | null = null;

  export default {
    async beforeRouteLeave(to, from, next) {
      const allowed = activeLeaveGuard ? await activeLeaveGuard() : true;
      next(allowed ? undefined : false);
    },
  };
</script>

<template>
  <div class="floor-plan-page page-container">
    <div class="page-inner">
      <v-card class="elevation-0">
        <v-card-title>
          <div class="header-container">
            <div class="header-title-container">
              <v-icon>mdi-floor-plan</v-icon>
              <span>{{ $t('floorPlan.title') }}</span>
            </div>
            <div class="header-actions-container">
              <v-btn v-if="dirty" text :disabled="saving" @click="discard">
                {{ $t('floorPlan.discard') }}
              </v-btn>
              <v-btn
                color="primary"
                :disabled="!dirty"
                :loading="saving"
                @click="save"
              >
                <v-icon left>mdi-content-save</v-icon>
                <span>{{ $t('save') }}</span>
              </v-btn>
            </div>
          </div>
        </v-card-title>
        <v-card-subtitle>{{ $t('floorPlan.subtitle') }}</v-card-subtitle>
        <v-card-text>
          <template v-if="!sectors || !tables">
            <v-skeleton-loader type="article" />
          </template>

          <template v-else-if="sectors.length === 0">
            <div class="empty-state">
              <v-icon size="56" class="empty-state-icon">
                mdi-floor-plan
              </v-icon>
              <h3 class="empty-state-title">
                {{ $t('floorPlan.empty.title') }}
              </h3>
              <p class="empty-state-description">
                {{ $t('floorPlan.empty.description') }}
              </p>
              <v-btn color="primary" @click="showSectorModal(null)">
                <v-icon left>mdi-plus</v-icon>
                <span>{{ $t('floorPlan.newSector') }}</span>
              </v-btn>
            </div>
          </template>

          <template v-else>
            <div class="plan-editor">
              <FloorPlanSectorTabs
                :sectors="sectors"
                :value="activeSectorId"
                @input="activeSectorId = $event"
                @add="showSectorModal(null)"
                @edit="showSectorModal($event)"
                @delete="deleteSector"
                @move="moveSector"
              />

              <FloorPlanCanvas
                v-if="activeSector"
                ref="canvasRef"
                :key="activeSector.id"
                :sector="activeSector"
                :entries="activeSectorEntries"
                :tables-by-id="tablesById"
                :selected-table-id="selectedTableId ?? undefined"
                :disabled="saving"
                @select="selectedTableId = $event"
                @change="onEntryChange"
                @toggle-shape="onToggleShape"
                @unplace="onUnplace"
              />

              <FloorPlanUnplacedTray
                :tables="unplacedTables"
                :disabled="saving"
                @drop-table="onTrayDrop"
                @new-table="showNewTableModal"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .page-container {
    @apply tw-flex tw-justify-center tw-p-4;

    .page-inner {
      @apply tw-w-full tw-max-w-6xl;

      .header-container {
        @apply tw-flex tw-justify-between tw-items-center tw-w-full tw-flex-wrap tw-gap-y-2;

        .header-title-container {
          /* v-card__title uses word-break: break-all, which stacks the title
             letter-by-letter when squeezed; wrap the whole row instead. */
          @apply tw-flex tw-items-center tw-space-x-1.5 tw-break-normal tw-whitespace-nowrap;
        }

        .header-actions-container {
          @apply tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0 tw-ml-auto;
        }
      }

      .empty-state {
        @apply tw-flex tw-flex-col tw-items-center tw-text-center tw-py-12 tw-px-4;

        .empty-state-icon {
          @apply tw-text-gray-300 tw-mb-3;
        }

        .empty-state-title {
          @apply tw-text-base tw-font-semibold tw-text-gray-700 tw-mb-1;
        }

        .empty-state-description {
          @apply tw-text-sm tw-text-gray-500 tw-mb-4 tw-max-w-md;
        }
      }

      .plan-editor {
        @apply tw-space-y-4;
      }
    }
  }
</style>
